// Infinite scroll for collection product grid and search results grid.
// Can be triggered either by observer or button click. The trigger is an
// <a href="?page=N"> (crawlable by Googlebot); JS intercepts the click and
// AJAX-loads in place. After each successful fetch we replaceState() the
// URL bar so deep pages have real URLs (sharing, back-button, SEO parity
// with Google's pagination guidance).
const getInfiniteScrollLiveRegion = () => {
  let liveRegion = document.getElementById('facet-results-status');
  if (liveRegion || !document.body) return liveRegion;

  liveRegion = document.createElement('p');
  liveRegion.id = 'facet-results-status';
  liveRegion.className = 'visually-hidden';
  liveRegion.setAttribute('role', 'status');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('tabindex', '-1');
  document.body.prepend(liveRegion);
  return liveRegion;
};

class InfiniteScroll extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('a, button');
    this.loadingOverlay = this.querySelector('.loading-overlay');
    this.onClick = (e) => {
      e.preventDefault();
      this.fetchNextPage();
    };
    this.button?.addEventListener('click', this.onClick);
    this.nextUrl = this.dataset.nextUrl;
    this.isFetching = false;

    if (this.dataset.triggerType === 'infinite' && this.nextUrl) {
      this.initObserver();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.button?.removeEventListener('click', this.onClick);
  }

  initObserver() {
    if (typeof IntersectionObserver !== 'function') return;

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.fetchNextPage();
      });
    }, { threshold: 0.5 });

    this.observer.observe(this);
  }

  async fetchNextPage() {
    if (this.isFetching || !this.nextUrl || !this.button) return;

    this.isFetching = true;
    this.button.classList.add('loading');
    this.loadingOverlay?.classList.remove('hidden');
    const requestedUrl = this.nextUrl;

    return fetch(requestedUrl)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Infinite scroll request failed with status ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const documentProductGrid = document.getElementById('product-grid-ajax'),
              documentPagination = document.getElementById('pagination-ajax'),
              html = new DOMParser().parseFromString(responseText, 'text/html'),
              fetchProductGrid = html.getElementById('product-grid-ajax'),
              fetchPagination = html.getElementById('pagination-ajax');

        if (!documentProductGrid || !documentPagination || !fetchProductGrid || !fetchPagination) {
          throw new Error('Infinite scroll response did not contain the product grid and pagination');
        }

        fetchProductGrid.querySelectorAll('.card--banner').forEach((banner) => banner.remove());

        documentProductGrid.insertAdjacentHTML('beforeend', fetchProductGrid.innerHTML);
        documentPagination.innerHTML = fetchPagination.innerHTML;

        // a11y: announce that more products loaded + move focus to the next-page link so
        // keyboard users continue from where they were rather than being jumped to top. WCAG 4.1.2 / 2.4.3.
        const liveRegion = getInfiniteScrollLiveRegion();
        if (liveRegion) {
          liveRegion.textContent = (window.themeStrings && window.themeStrings.products_loaded) || 'More products loaded.';
        }
        const nextLink = documentPagination.querySelector('a[rel="next"]');
        if (nextLink) nextLink.focus({ preventScroll: true });

        // Reflect the just-loaded page in the URL bar so deep pages have real
        // URLs (back/forward, sharing, and parity with the canonical we emit).
        window.history.replaceState({}, '', requestedUrl);

        // Save the whole collection div for back button usage
        const collection = document.getElementById('collection-ajax');
        try {
          if (collection && window.sessionStorage) {
            sessionStorage.setItem('pageContent', collection.innerHTML);
            sessionStorage.setItem('pageContentURL', window.location.href);
          }
        } catch (storageError) {
          // Storage can be unavailable in private browsing; pagination still works.
        }

        this.observer?.disconnect();

      })
      .catch((error) => {
        // Keep the crawlable link and nextUrl unchanged so the request can retry.
        const liveRegion = getInfiniteScrollLiveRegion();
        if (liveRegion) {
          liveRegion.textContent =
            (window.themeStrings && window.themeStrings.load_error) ||
            'More products could not be loaded. Try again.';
        }
        console.error('Infinite scroll request failed', error);
      })
      .finally(() => {
        this.isFetching = false;
        this.button.classList.remove('loading');
        this.loadingOverlay?.classList.add('hidden');
      });
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    event.target.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
  }
}
customElements.define('infinite-scroll', InfiniteScroll);

// Load content from local storage logic
(() => {
  // Back button detection
  if (!performance?.navigation || performance.navigation.type !== 2) {
    return;
  }

  let contentURL;
  try {
    if (!window.sessionStorage) return;
    contentURL = sessionStorage.getItem('pageContentURL');
  } catch (storageError) {
    return;
  }

  // URL of the content must mach wiht the page
  if (window.location.href !== contentURL) {
    return;
  }

  let content;
  try {
    content = sessionStorage.getItem('pageContent');
  } catch (storageError) {
    return;
  }
  const collection = document.getElementById('collection-ajax');
  if (content && collection) {
      collection.innerHTML = content;
  }
})();
